import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { Lookup } from '../app.types';
import { Observable } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponentDialog } from '../confirm-dialog/confirm-dialog.component';
import { ToastrService } from 'ngx-toastr';

type ManageMode = "View" | "Add" | "Edit";

@Component({
    selector: 'lookup-manager',
    templateUrl: './lookup-manager.component.html',
    styleUrls: ['./lookup-manager.component.scss'],
    standalone: false
})
export class LookupManagerComponent implements ControlValueAccessor, OnChanges {
  @Input() lookupName = "Item";
  @Input() defaultText = "Please choose a value";
  @Input() addInputPlaceholder = "Enter a category name"
  @Input() items: Lookup[] = [];

  @Input() displayNameMapper = (name: string) => name;
  @Input() inputRegexValidations: { regex: RegExp, message: string }[] = []

  @Input() addNew: (name: string) => Observable<string>;
  @Input() edit: (item: Lookup) => Observable<string>;
  @Input() delete: (item: Lookup) => Observable<boolean>;

  @Output() lookupsChanged = new EventEmitter<void>(); 
  
  @Input() errorMessages: Record<string, string> = {}; 

  currentItemId: string;

  currentMode: ManageMode = "View";
  displayedText: string;
  newValue = "";

  editingItem: Lookup;
  
  _isDropdownOpen = false;
  
  public set isDropdownOpen(v : boolean) {
    if(this._isDropdownOpen && v !== true){
      this.onTouched();
      this.cleanUp();
    }
    this._isDropdownOpen = v;
  }

  
  public get isDropdownOpen() : boolean {
    return this._isDropdownOpen;
  }
  
  constructor(public ngControl: NgControl, public dialog: MatDialog, private readonly toastrService: ToastrService) {
    ngControl.valueAccessor = this;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const itemsChange = changes['items'];
    if(itemsChange && itemsChange.currentValue !== itemsChange.previousValue){
      this.items = this.items.map(x => {
        return {...x, displayName: this.displayNameMapper(x.name)}
      });
      const selectedItem = this.items.find(x => x.id === this.currentItemId);
      this.displayedText = selectedItem?.displayName ?? this.defaultText;
    }
  }
  
  private onChange!: (value: string) => void;
  private onTouched!: () => void;
  
  writeValue(id: string): void {
    this.currentItemId = id;
    if(this.items && id){
      const selectedItem = this.items.find(x => x.id === id);
      this.displayedText = selectedItem.displayName;
      return;
    }

    this.displayedText = this.defaultText;

  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
  }

  toggleMode(mode: ManageMode){
    if(this.currentMode === mode){
      this.cleanUp();
      return;
    }
    this.currentMode = mode;
  }

  loadEditMode(){
    if(this.currentMode !== "Edit") {this.currentMode = "Edit";}
  }

  cancelEdit(){
    console.log('cancelling edit!!')
    this.editingItem = null;
  }

  selectItem(item: Lookup){
    if(this.currentMode !== "Edit"){
      this.displayedText = item.displayName;
      this.currentItemId = item.id;
      this.onChange(item.id);
      this.isDropdownOpen = false;
    } else {
      this.editingItem = {...item};
    }
  }

  addItem(item: string){
    this.addNew(item)
      .subscribe({
        next: res => {
          this.toastrService.success(`${this.lookupName} added.`, 'Success');
          this.onChange(res);
          this.displayedText = item;
          this.lookupsChanged.emit();
          this.currentItemId = res;
          this.isDropdownOpen = false;
        }, 
        error: console.error
      })
  }

  editItem(item: Lookup){
    this.edit(item)
      .subscribe({
        next: res => {
          this.toastrService.success(`${this.lookupName} updated.`, 'Success');
          this.lookupsChanged.emit();
          this.editingItem = null;
        }, 
        error: console.error
      })
  }

  deleteItem(item: Lookup){
    const dialogRef = this.dialog.open(ConfirmDialogComponentDialog, {
          data: {
            title: `Delete ${this.lookupName}`,
            message: `This action cannot be undone. The ${this.lookupName.toLowerCase()} will be permanently removed.`,
            actions: [
              { text: "Cancel", result: false },
              { text: "Delete", result: true, color: "#D30A0A" }
            ]
          },
          autoFocus: false
        });
        
    dialogRef.afterClosed().subscribe((result) => {
      if (result == null) {return;}

      if (result == false) {console.log(`don't delete ${this.lookupName.toLowerCase()}`);}

      if (result == true){
        this.delete(item)
          .subscribe({
            next: res => {
              this.toastrService.success(`${this.lookupName} removed.`, 'Success');
              this.onChange("");
              this.lookupsChanged.emit();
            }, 
            error: console.error
          })
      }
    });
  }

  itemAlreadyExists(name: string){
    return this.items.some(
      x => x.name.toLowerCase() === name.toLowerCase() && x.id !== this.editingItem?.id);
  }

  editingItemNameChanged(item: Lookup){
    const originalItem = this.items.find(x => x.id === item.id);
    return originalItem.name !== item.name;
  }
  
  cleanUp(){
    this.newValue = "";
    this.editingItem = null;
    this.currentMode = "View";
  }

  getUnsatisfiedRegexes(value: string) {
    return this.inputRegexValidations.filter(x => !x.regex.test(value));
  }
}
